import isObject from './isObject';
import isArray from './isArray';
import { ANY_WHITESPACE_REGEX } from '../constants/regex';
import { convertToNullOrValidString } from '../helpers/sanitization/convertToNullOrValidString';
import { convertToNullOrValidBoolean } from '../helpers/sanitization/convertToNullOrValidBoolean';
import { convertToNullOrValidNumber } from '../helpers/sanitization/convertToNullOrValidNumber';
import { cleanAddressOrUnitNumber } from '../helpers/repliersData/cleanAddressOrUnitNumber';

export class RepliersDataCleaner<
  TRepliers extends { [key: string]: any },
  TResult,
> {
  repliersData: TRepliers;
  options: {
    types?: { [key: string]: 'boolean' | 'number' };
    fieldTransformations?: { [key: string]: 'string' };
  };

  static cleanStreetNumber(streetNumber: string): string {
    return cleanAddressOrUnitNumber(streetNumber);
  }

  static cleanUnitNumber(unitNumber: string | null): string | null {
    return convertToNullOrValidString(cleanAddressOrUnitNumber(unitNumber));
  }

  constructor(repliersData, options) {
    this.repliersData = repliersData;
    this.options = options;
  }

  clean(): TResult {
    const cleaned = {};
    for (const [field, repliersValue] of Object.entries(this.repliersData)) {
      const cleanedField = this.transformRepliersField(field);
      cleaned[cleanedField] = this.cleanRepliersValue(field, repliersValue);
    }
    return cleaned as TResult;
  }

  transformRepliersField(field: string): string {
    if (this.options.fieldTransformations?.[field]) {
      // there is a field transformation passed in that we want to apply
      return this.options.fieldTransformations[field];
    } else {
      // no changes to the field requested, return the original field
      return field;
    }
  }

  cleanRepliersValue(field, repliersValue): any {
    if (isObject(repliersValue)) {
      const cleaned = {};
      for (const [k, v] of Object.entries(repliersValue)) {
        const cleanedField = this.transformRepliersField(k);
        cleaned[cleanedField] = this.cleanRepliersValue(k, v);
      }
      return cleaned;
    } else if (isArray(repliersValue)) {
      return repliersValue.map(v => this.cleanRepliersValue(field, v));
    } else if (this.options.types?.[field] === 'number') {
      return convertToNullOrValidNumber(repliersValue);
    } else if (this.options.types?.[field] === 'boolean') {
      return convertToNullOrValidBoolean(repliersValue);
    } else {
      return convertToNullOrValidString(repliersValue);
    }
  }
}
